import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { CloudStatus } from "@desk/contracts";
import { Button } from "@/components/ui/button";
import { DeskMark } from "@/components/icons";
import { describeError } from "@/data/api";
import { invalidateContainers, qk } from "@/data/cache";
import { useDesk } from "@/data/context";
import { useBootstrap } from "@/data/hooks";
import { useStored } from "@/lib/storage";
import { SignInForm } from "./SignInScreen";

const DISMISSED = "welcome.dismissed";
const existingTasks = { limit: 1, includeClosed: true, includeSnoozed: true, includeDeleted: true, includeArchived: true };

/**
 * True when this window talks to a Mac's local API that is signed out of Desk cloud and has no
 * tasks, areas, spaces or projects yet: a fresh install. Undefined while that is still loading.
 */
export function useNeedsWelcome(): boolean | undefined {
  const { api, session } = useDesk();
  const [dismissed] = useStored(DISMISSED, false);
  const mac = session.source === "electron" || session.source === "dev";
  const status = useQuery({
    queryKey: qk.cloud,
    queryFn: () => api.cloudStatus(),
    enabled: mac && !dismissed,
  });
  // Persisted empty lists can predate work created by another client or the local API.
  const boot = useBootstrap({ refetchOnMount: "always" });
  const emptyContainers = !!boot.data && !boot.data.areas.length && !boot.data.spaces.length && !boot.data.projects.length;
  const tasks = useQuery({
    queryKey: qk.tasks(existingTasks),
    queryFn: () => api.listTasks(existingTasks),
    enabled: mac && !dismissed && status.data?.state === "signed_out" && boot.isFetchedAfterMount && emptyContainers,
    staleTime: 0,
    refetchOnMount: "always",
  });
  if (!mac || dismissed || status.isError || boot.isError) return false;
  if (!status.data) return undefined;
  if (status.data.state !== "signed_out") return false;
  if (!boot.data || !boot.isFetchedAfterMount) return undefined;
  if (!emptyContainers || tasks.isError) return false;
  if (!tasks.data || !tasks.isFetchedAfterMount) return undefined;
  return !tasks.data.items.length;
}

const finished = (s: CloudStatus) => !!s.lastSyncAt || ["error", "offline", "update_required"].includes(s.state);

/** First launch on a Mac: sign in to Desk cloud to bring everything down, or carry on without an account. */
export function MacWelcome({ onDone }: { onDone: () => void }) {
  const { api } = useDesk();
  const qc = useQueryClient();
  const [, setDismissed] = useStored(DISMISSED, false);
  const [step, setStep] = useState<"sign-in" | "choose" | "syncing">("sign-in");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const finish = async () => {
    await invalidateContainers(qc);
    onDone();
  };

  // Wait for the first pull so the workspace opens with everything in it.
  useEffect(() => {
    if (step !== "syncing") return;
    let stopped = false;
    const poll = async () => {
      while (!stopped) {
        try {
          const s = await api.cloudStatus();
          if (stopped) return;
          qc.setQueryData(qk.cloud, s);
          if (s.state === "signed_out") {
            setError(s.lastError);
            setStep("sign-in");
            return;
          }
          if (finished(s)) break;
        } catch {
          break;
        }
        await new Promise((r) => setTimeout(r, 750));
      }
      if (!stopped) await finish();
    };
    void poll();
    return () => {
      stopped = true;
    };
  }, [step]);

  const link = async (strategy: "merge" | "download") => {
    setBusy(true);
    setError(null);
    try {
      qc.setQueryData(qk.cloud, await api.cloudLink(strategy));
      setStep("syncing");
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full flex-col bg-background">
      <div className="desk-drag h-12 shrink-0" />
      <div className="flex flex-1 items-center justify-center p-6 pb-18">
        <div className="w-full max-w-[380px]">
          <DeskMark className="mb-5 h-9 w-auto" />
          {step === "sign-in" && (
            <>
              <h1 className="mb-6 font-display text-[26px] font-semibold tracking-[-0.015em]">Sign in to Desk</h1>
              {error && (
                <p role="alert" className="mb-4 rounded-md bg-urgent-soft px-3 py-2 text-[13px]">
                  {error}
                </p>
              )}
              <SignInForm
                onCode={async (email) => {
                  setError(null);
                  await api.cloudSignIn(email);
                }}
                onVerify={async (email, code) => {
                  const s = await api.cloudVerify(email, code);
                  qc.setQueryData(qk.cloud, s);
                  setStep(s.state === "needs_choice" ? "choose" : "syncing");
                }}
              />
              <Button
                variant="link"
                size="sm"
                className="mt-6 px-0 text-muted-foreground"
                onClick={() => {
                  setDismissed(true);
                  onDone();
                }}
              >
                Use Desk without an account
              </Button>
            </>
          )}
          {step === "choose" && (
            <>
              <h1 className="mb-2 font-display text-[22px] font-semibold tracking-[-0.015em]">This Mac and Desk cloud both have tasks</h1>
              <p className="mb-6 text-[13.5px] text-muted-foreground">
                Merge keeps everything from both. Use cloud data replaces what's on this Mac with what's in Desk cloud.
              </p>
              {error && (
                <p role="alert" className="mb-4 rounded-md bg-urgent-soft px-3 py-2 text-[13px]">
                  {error}
                </p>
              )}
              <div className="flex gap-2">
                <Button variant="default" disabled={busy} onClick={() => void link("merge")}>
                  Merge
                </Button>
                <Button variant="ghost" disabled={busy} onClick={() => void link("download")}>
                  Use cloud data
                </Button>
              </div>
            </>
          )}
          {step === "syncing" && (
            <h1 aria-busy="true" className="font-display text-[22px] font-semibold tracking-[-0.015em] text-muted-foreground">
              Getting your stuff…
            </h1>
          )}
        </div>
      </div>
    </div>
  );
}
